import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

export interface BillingMethod {
  _id: string;
  name: string;
  country: string;
  cardDigits: string;
  cardExpiryMonth: number;
  cardExpiryYear: number;
  favorite: boolean;
  active: boolean;
  invalid: boolean;
  isExpiredCard: boolean;
  daysToExpire: number;
  zipCode: string | null;
  isZipInvalidByCountry: boolean;
  activeLicenses: number;
  graceLicenses: number;
}

export interface SelectedPlanProps {
  planId: string;
  planName: string;
  monthly: boolean;
  limitation?: number;
  visitsLimitation?: number;
  pricingModelPlan?: string;
}

export interface billingAddress {
  city: null | string;
  country: null | string;
  line1: null | string;
  line2: null | string;
  postal_code: null | string;
  state: null | string;
}

export interface CustomerBillingAddress {
  address: billingAddress;
  name: string;
}

interface BillingContextProps {
  billingMethods: BillingMethod[] | undefined;
  setBillingMethods: Dispatch<SetStateAction<BillingMethod[] | undefined>>;
  isNewPaymentMethodAdded: boolean;
  setIsNewPaymentMethodAdded: Dispatch<SetStateAction<boolean>>;
  selectedBillingId: string;
  setSelectedBillingId: Dispatch<SetStateAction<string>>;
  selectedPlan: SelectedPlanProps | undefined;
  setSelectedPlan: Dispatch<SetStateAction<SelectedPlanProps | undefined>>;
  isProcessingPayment: boolean;
  setIsProcessingPayment: Dispatch<SetStateAction<boolean>>;
  wooPendingPayment: boolean | null;
  setWooPendingPayment: Dispatch<SetStateAction<boolean | null>>;
  billingAddress: CustomerBillingAddress | null;
  setBillingAddress: Dispatch<SetStateAction<CustomerBillingAddress | null>>;
  billingsCount: number | null;
  setBillingsCount: Dispatch<SetStateAction<number | null>>;
  billingAddressLoaded: boolean;
  setBillingAddressLoaded: Dispatch<SetStateAction<boolean>>;
}

const BillingContext = createContext<BillingContextProps | undefined>(
  undefined,
);

export const BillingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [billingMethods, setBillingMethods] = useState<
    BillingMethod[] | undefined
  >();
  const [isNewPaymentMethodAdded, setIsNewPaymentMethodAdded] =
    useState<boolean>(false);
  const [selectedBillingId, setSelectedBillingId] = useState<string>('');
  const [selectedPlan, setSelectedPlan] = useState<
    SelectedPlanProps | undefined
  >();
  const [isProcessingPayment, setIsProcessingPayment] =
    useState<boolean>(false);
  const [wooPendingPayment, setWooPendingPayment] = useState<boolean | null>(
    null,
  );
  const [billingAddress, setBillingAddress] =
    useState<CustomerBillingAddress | null>(null);
  const [billingsCount, setBillingsCount] = useState<number | null>(null);
  const [billingAddressLoaded, setBillingAddressLoaded] =
    useState<boolean>(false);

  return (
    <BillingContext.Provider
      value={{
        billingMethods,
        setBillingMethods,
        isNewPaymentMethodAdded,
        setIsNewPaymentMethodAdded,
        selectedBillingId,
        setSelectedBillingId,
        selectedPlan,
        setSelectedPlan,
        isProcessingPayment,
        setIsProcessingPayment,
        wooPendingPayment,
        setWooPendingPayment,
        billingAddress,
        setBillingAddress,
        billingsCount,
        setBillingsCount,
        billingAddressLoaded,
        setBillingAddressLoaded,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = (): BillingContextProps => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};
